import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatNaira } from "@/lib/api";
import { useBasket } from "@/context/BasketContext";
import { toast } from "sonner";
import { Loader2, School, MapPin, Phone, ArrowLeft, ArrowRight, Check, BookOpen } from "lucide-react";

export const SchoolOrdersModal = ({ open, onOpenChange }) => {
  const { addMany } = useBasket();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [schoolSlug, setSchoolSlug] = useState("");
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [preview, setPreview] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1); setSchoolSlug(""); setSelectedClasses([]); setPreview([]);
    setLoading(true);
    api.get("/schools").then((r) => setSchools(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [open]);

  const school = schools.find((s) => s.slug === schoolSlug);
  const primary = schools.filter((s) => s.level === "primary");
  const secondary = schools.filter((s) => s.level === "secondary");

  const toggleClass = (c) => setSelectedClasses((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));

  const goPreview = async () => {
    setLoadingPreview(true);
    try {
      const r = await api.get(`/schools/${schoolSlug}/items`, { params: { classes: selectedClasses.join(",") } });
      setPreview(r.data); setStep(3);
    } catch { toast.error("Could not load the list."); }
    finally { setLoadingPreview(false); }
  };

  const confirm = () => {
    if (preview.length === 0) { toast.error("No items to add."); return; }
    addMany(preview);
    const totalQty = preview.reduce((s, x) => s + x.qty, 0);
    toast.success(`${totalQty} item${totalQty === 1 ? "" : "s"} added to your list`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="school-orders-modal">
        <DialogHeader>
          <DialogTitle>School Orders</DialogTitle>
          <DialogDescription>
            {step === 1 && "Choose your school."}
            {step === 2 && "Select the class(es) you're buying for."}
            {step === 3 && "Review the items, then add them to your list."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>
        ) : (
          <>
            {step === 1 && (
              <div className="space-y-4">
                {schools.length === 0 && <p className="text-sm text-brand-black/60">No schools available yet.</p>}
                {[["Primary", primary], ["Secondary", secondary]].map(([label, group]) => group.length > 0 && (
                  <div key={label}>
                    <p className="text-xs font-bold uppercase tracking-wide text-brand-black/50 mb-2">{label}</p>
                    <div className="space-y-2">
                      {group.map((s) => (
                        <label key={s.slug} data-testid={`school-option-${s.slug}`} className={`flex items-center gap-3 p-3 border-2 rounded-md cursor-pointer ${schoolSlug === s.slug ? "border-brand-blue bg-brand-blue/5" : "border-black/15 hover:border-black/40"}`}>
                          <input type="radio" name="school" checked={schoolSlug === s.slug} onChange={() => setSchoolSlug(s.slug)} className="w-4 h-4" />
                          {s.logo ? <img src={s.logo} alt="" className="w-10 h-10 rounded object-cover border" /> : <span className="w-10 h-10 flex items-center justify-center bg-brand-cream border rounded"><School className="w-5 h-5 text-brand-blue" /></span>}
                          <span className="min-w-0">
                            <span className="block font-semibold text-brand-blue truncate">{s.name}</span>
                            {s.address && <span className="block text-xs text-brand-black/50 truncate"><MapPin className="w-3 h-3 inline mr-1" />{s.address}</span>}
                            {s.phone && <span className="block text-xs text-brand-black/50 truncate"><Phone className="w-3 h-3 inline mr-1" />{s.phone}</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                <button disabled={!schoolSlug} onClick={() => setStep(2)} data-testid="school-next-classes" className="w-full inline-flex items-center justify-center gap-2 bg-brand-blue text-white font-semibold px-5 py-3 border-2 border-black rounded-md disabled:opacity-50">
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}

            {step === 2 && school && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {(school.classes || []).length === 0 && <p className="text-sm text-brand-black/60">This school has no lists set up yet.</p>}
                  {(school.classes || []).map((c) => (
                    <label key={c} data-testid={`class-option-${c}`} className={`inline-flex items-center gap-2 px-3 py-2 border-2 rounded-md cursor-pointer text-sm font-semibold ${selectedClasses.includes(c) ? "border-brand-blue bg-brand-blue/5" : "border-black/15 hover:border-black/40"}`}>
                      <input type="checkbox" checked={selectedClasses.includes(c)} onChange={() => toggleClass(c)} className="w-4 h-4" /> {c}
                    </label>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 px-4 py-3 border-2 border-black rounded-md font-semibold"><ArrowLeft className="w-4 h-4" /> Back</button>
                  <button disabled={selectedClasses.length === 0 || loadingPreview} onClick={goPreview} data-testid="school-preview-btn" className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-blue text-white font-semibold px-5 py-3 border-2 border-black rounded-md disabled:opacity-50">
                    {loadingPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Preview items <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                {preview.length === 0 ? <p className="text-sm text-brand-black/60">No items found for the selected class(es).</p> : (
                  <div className="border-2 border-black/15 rounded-md divide-y max-h-72 overflow-y-auto" data-testid="school-preview-list">
                    {preview.map((it) => (
                      <div key={it.slug} className="flex items-center justify-between gap-3 p-2.5 text-sm">
                        <span className="flex items-center gap-3 min-w-0">
                          {it.images?.[0] ? (
                            <img src={it.images[0]} alt="" className="w-10 h-10 rounded border object-cover shrink-0" />
                          ) : (
                            <span className="w-10 h-10 rounded border bg-brand-cream flex items-center justify-center shrink-0"><BookOpen className="w-4 h-4 text-brand-blue/50" /></span>
                          )}
                          <span className="min-w-0"><span className="font-medium text-brand-blue block truncate">{it.name}</span><span className="text-xs text-brand-black/50">{formatNaira(it.price)}{it.unit ? ` · ${it.unit}` : ""}</span></span>
                        </span>
                        <span className="shrink-0 font-bold">×{it.qty}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={() => setStep(2)} className="inline-flex items-center gap-2 px-4 py-3 border-2 border-black rounded-md font-semibold"><ArrowLeft className="w-4 h-4" /> Back</button>
                  <button disabled={preview.length === 0} onClick={confirm} data-testid="school-add-basket" className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-gold text-black font-bold px-5 py-3 border-2 border-black rounded-md shadow-brutal-black disabled:opacity-50">
                    <Check className="w-4 h-4" /> Add to my list
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
